import { Close } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    Divider,
    Drawer,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    Tab,
    Tabs,
} from "@mui/material";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import Iconify from "../../components/iconify";
import copyFunction from "../../util/function/copyFunction";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";

interface SupportContact {
    name: string;
    level: string;
    phone: string;
    email: string;
}

interface DetailedEscalationMatrix {
    Broker: SupportContact[];
    Tpa: SupportContact[];
    Insurer: SupportContact[];
}

const ClaimContactCard = ({
    open,
    handleClaimContactOpen,
}: {
    open: boolean;
    handleClaimContactOpen: () => void;
}) => {
    const isMobile = useMediaQuery("(max-width:600px)");
    const [showEscalate, setShowEscalate] = React.useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const customerCareData = useSelector(
        (state: RootState) => state.customerCare.customerCareData
    );
    const theme = useTheme();

    const lead = JSON.parse(sessionStorage.getItem("lead") || "{}");

    const tabs = useMemo(
        () => [
            { label: "Broker", key: "Broker" },
            { label: "TPA", key: "Tpa" },
            { label: "Insurer", key: "Insurer" },
        ],
        []
    );

    const getAvailableTabs = useCallback(() => {
        if (!customerCareData?.detailedEscalationMatrixDTOS) return [];
        return tabs.filter((tab) => {
            const tabKey = tab.key as keyof DetailedEscalationMatrix;
            const contacts =
                customerCareData.detailedEscalationMatrixDTOS?.[tabKey] || [];
            return contacts.some(
                (contact: SupportContact) =>
                    contact.name && contact.name.trim() !== ""
            );
        });
    }, [customerCareData?.detailedEscalationMatrixDTOS, tabs]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    // Reset active tab if current tab becomes unavailable
    useEffect(() => {
        const availableTabs = getAvailableTabs();
        if (availableTabs.length > 0 && activeTab >= availableTabs.length) {
            setActiveTab(0);
        }
    }, [
        customerCareData?.detailedEscalationMatrixDTOS,
        activeTab,
        getAvailableTabs,
    ]);

    const getCurrentTabData = (): SupportContact[] => {
        if (!customerCareData?.detailedEscalationMatrixDTOS) return [];
        const availableTabs = getAvailableTabs();
        if (availableTabs.length === 0) return [];

        const currentTab = availableTabs[activeTab];
        if (!currentTab) return [];

        const tabKey = currentTab.key as keyof DetailedEscalationMatrix;
        const contacts =
            customerCareData.detailedEscalationMatrixDTOS?.[tabKey] || [];
        return contacts.filter(
            (contact: SupportContact) =>
                contact.name && contact.name.trim() !== ""
        );
    };

    const renderContactCard = (contact: SupportContact, index: number) => (
        <Box
            key={index}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
                width: "100%",
                gap: "8px",
                marginBottom: "16px",
            }}
        >
            <Stack
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                width={"100%"}
            >
                <Typography variant="subtitle2">{contact.name}</Typography>
                <Chip
                    label={
                        <Typography
                            variant="label"
                            sx={{
                                color:
                                    contact.level === "1"
                                        ? theme.palette.error.dark
                                        : contact.level === "2"
                                        ? theme.palette.warning.dark
                                        : theme.palette.success.dark,
                            }}
                        >
                            {`Level ${contact.level}`}
                        </Typography>
                    }
                    sx={{
                        height: "18px",
                        width: "fit-content",
                        backgroundColor:
                            contact.level === "1"
                                ? theme.palette.error.lighter
                                : contact.level === "2"
                                ? theme.palette.warning.lighter
                                : theme.palette.success.lighter,
                    }}
                />
            </Stack>
            {[...contact.phone.split(","), ...contact.email.split(",")]
                .filter((contactInfo) => contactInfo.trim() !== "")
                .map((contactInfo, contactIndex) => (
                    <Stack
                        key={contactIndex}
                        flexDirection={"row"}
                        gap={"8px"}
                        alignItems={"center"}
                    >
                        <Box
                            sx={{
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Iconify
                                icon={"material-symbols:content-copy-outline"}
                                style={{
                                    cursor: "pointer",
                                    width: "20px",
                                    height: "20px",
                                    color: theme.palette.brand?.main,
                                }}
                                onClick={() => {
                                    copyFunction(contactInfo.trim());
                                }}
                            />
                        </Box>
                        <Typography
                            variant="body2"
                            color={theme.palette.text.primary}
                        >
                            {contactInfo.trim()}
                        </Typography>
                    </Stack>
                ))}
        </Box>
    );
    return (
        <Drawer
            open={open}
            onClose={handleClaimContactOpen}
            anchor={isMobile ? "bottom" : "right"}
            sx={{
                "& .MuiDrawer-paper": {
                    padding: "24px 20px;",
                    display: "flex",
                    width: isMobile ? "100%" : "375px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    flexShrink: 0,
                    borderRadius: {
                        xs: "24px 24px 0px 0px",
                        sm: "24px 0px 0px 24px",
                    },
                    backgroundColor: "#FFF",
                    gap: "24px",
                },
            }}
        >
            {!showEscalate ? (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "16px",
                            alignSelf: "stretch",
                            width: "100%",
                        }}
                    >
                        <Stack
                            flexDirection={"row"}
                            justifyContent={"space-between"}
                            alignItems={"flex-start"}
                            width={"100%"}
                        >
                            <Stack flexDirection={"column"} spacing={"4px"}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.text.primary,
                                    }}
                                    margin={0}
                                >
                                    {customerCareData?.name}
                                </Typography>
                                <Typography
                                    variant="body3"
                                    color={(theme) =>
                                        theme.palette.text.secondary
                                    }
                                >
                                    {`Claim representative at ${lead?.companyName} `}
                                </Typography>
                            </Stack>
                            <Close
                                onClick={handleClaimContactOpen}
                                style={{ cursor: "pointer" }}
                                fontSize="medium"
                            />
                        </Stack>
                        <Stack
                            flexDirection={"column"}
                            gap={"4px"}
                            width={"100%"}
                        >
                            {[customerCareData?.phone, customerCareData?.email]
                                .filter((item) => item && item.trim() !== "")
                                .map((item) => (
                                    <Stack flexDirection={"column"} gap={"4px"}>
                                        {item
                                            ?.split(",")
                                            .filter(
                                                (contactItem) =>
                                                    contactItem.trim() !== ""
                                            )
                                            .map((item) => (
                                                <Stack
                                                    flexDirection={"row"}
                                                    gap={"8px"}
                                                    alignItems={"center"}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: "36px",
                                                            height: "36px",
                                                            display: "flex",
                                                            justifyContent:
                                                                "center",
                                                            alignItems:
                                                                "center",
                                                        }}
                                                    >
                                                        <Iconify
                                                            icon={
                                                                "material-symbols:content-copy-outline"
                                                            }
                                                            style={{
                                                                cursor: "pointer",
                                                                width: "20px",
                                                                height: "20px",
                                                                // padding: "8px",
                                                                color: theme
                                                                    .palette
                                                                    .brand
                                                                    ?.main,
                                                            }}
                                                            onClick={() => {
                                                                copyFunction(
                                                                    item as string
                                                                );
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        color={(theme) =>
                                                            theme.palette.text
                                                                .primary
                                                        }
                                                    >
                                                        {item}
                                                    </Typography>
                                                </Stack>
                                            ))}
                                    </Stack>
                                ))}
                        </Stack>
                    </Box>
                    <Stack
                        flexDirection={"column"}
                        spacing={"16px"}
                        width={"100%"}
                    >
                        {isMobile && (
                            <a
                                href={`tel:${customerCareData?.phone}`}
                                style={{ textDecoration: "none" }}
                            >
                                <Button
                                    variant="contained"
                                    size="small"
                                    fullWidth
                                    sx={{
                                        backgroundColor: (theme) =>
                                            theme.palette.brand.main,
                                        color: "#FFF",
                                        height: "42px",
                                    }}
                                    endIcon={
                                        <Iconify
                                            icon={
                                                "material-symbols:arrow-right-alt"
                                            }
                                            style={{ color: "#FFF" }}
                                        />
                                    }
                                >
                                    <Typography
                                        sx={{
                                            textTransform: "none",
                                        }}
                                        variant="overline"
                                        color={"white"}
                                    >
                                        Call now
                                    </Typography>
                                </Button>
                            </a>
                        )}
                        {customerCareData?.detailedEscalationMatrixDTOS &&
                            Object.values(
                                customerCareData.detailedEscalationMatrixDTOS
                            ).some(
                                (category: any) =>
                                    Array.isArray(category) &&
                                    category.some(
                                        (contact: SupportContact) =>
                                            contact.name &&
                                            contact.name.trim() !== ""
                                    )
                            ) && (
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "#DC2B2B",
                                        textAlign: "center",
                                        width: "100%",
                                        cursor: "pointer",
                                        textTransform: "none",
                                    }}
                                    onClick={() => {
                                        setShowEscalate(true);
                                        setActiveTab(0); // Reset to first tab when opening escalation
                                    }}
                                >
                                    Escalate issue
                                </Typography>
                            )}
                    </Stack>
                </>
            ) : (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "16px",
                            alignSelf: "stretch",
                            width: "100%",
                        }}
                    >
                        <Stack
                            flexDirection={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            width={"100%"}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    color: theme.palette.text.primary,
                                }}
                                margin={0}
                            >
                                Claim escalation
                            </Typography>
                            <Close
                                onClick={handleClaimContactOpen}
                                style={{ cursor: "pointer" }}
                                fontSize="medium"
                            />
                        </Stack>
                        {!isMobile && (
                            <Divider variant="dashed" sx={{ width: "100%" }} />
                        )}
                        {getAvailableTabs().length > 0 && (
                            <Box
                                sx={{
                                    width: "100%",
                                    borderBottom: 1,
                                    borderColor: "divider",
                                }}
                            >
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    variant="fullWidth"
                                    textColor="primary"
                                    indicatorColor="primary"
                                    sx={{
                                        "& .MuiTab-root": {
                                            textTransform: "none",
                                            fontWeight: 500,
                                        },
                                        "& .MuiTabs-indicator": {
                                            backgroundColor:
                                                theme.palette.brand.main,
                                        },
                                        "& .Mui-selected": {
                                            color: `${theme.palette.brand.main} !important`,
                                        },
                                    }}
                                >
                                    {getAvailableTabs().map((tab, index) => (
                                        <Tab key={index} label={tab.label} />
                                    ))}
                                </Tabs>
                            </Box>
                        )}
                        {getAvailableTabs().length > 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    padding: "16px 15px",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    gap: "16px",
                                    alignSelf: "stretch",
                                    flexDirection: "row",
                                    width: "100%",
                                    borderRadius: "16px",
                                    backgroundColor: "#FFF3E4",
                                }}
                            >
                                <Iconify
                                    icon={"material-symbols:warning-rounded"}
                                    style={{
                                        color: "#D5691B",
                                        width: "32px",
                                        height: "32px",
                                    }}
                                />
                                <Typography
                                    variant="overline"
                                    sx={{
                                        textTransform: "none",
                                    }}
                                >
                                    Please follow the given hierarchy only.
                                </Typography>
                            </Box>
                        )}
                        <Box
                            sx={{
                                width: "100%",
                                maxHeight: "60vh",
                                overflowY: "auto",
                                paddingTop: "8px",
                            }}
                        >
                            {getCurrentTabData().map((contact, index) =>
                                renderContactCard(contact, index)
                            )}
                        </Box>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={
                            <KeyboardBackspaceIcon
                                sx={{
                                    color: (theme) => theme.palette.grey[800],
                                }}
                            />
                        }
                        onClick={() => {
                            setShowEscalate(false);
                        }}
                    >
                        <Typography
                            sx={{
                                textTransform: "none",
                            }}
                            variant="overline"
                        >
                            Back
                        </Typography>
                    </Button>
                </>
            )}
        </Drawer>
    );
};

export default ClaimContactCard;
